// Paragraph panel — text alignment, indentation, spacing.

// ---------- DOM helpers ----------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

// ---------- Public types ----------

export interface ParagraphProperties {
  align: "left" | "center" | "right" | "justify";
  leftIndent: number;
  rightIndent: number;
  firstLineIndent: number;
  spaceBefore: number;
  spaceAfter: number;
}

const DEFAULTS: ParagraphProperties = {
  align: "left",
  leftIndent: 0,
  rightIndent: 0,
  firstLineIndent: 0,
  spaceBefore: 0,
  spaceAfter: 0,
};

// ---------- Component ----------

export class ParagraphPanel {
  private built = false;
  private root!: HTMLDivElement;

  private alignBtns!: HTMLButtonElement[];
  private leftIndentInput!: HTMLInputElement;
  private rightIndentInput!: HTMLInputElement;
  private firstLineIndentInput!: HTMLInputElement;
  private spaceBeforeInput!: HTMLInputElement;
  private spaceAfterInput!: HTMLInputElement;

  private props: ParagraphProperties = { ...DEFAULTS };
  private onChange: ((props: ParagraphProperties) => void) | undefined;

  constructor(onChange?: (props: ParagraphProperties) => void) {
    this.onChange = onChange;
  }

  getProps(): ParagraphProperties {
    return { ...this.props };
  }

  setProps(partial: Partial<ParagraphProperties>): void {
    Object.assign(this.props, partial);
    if (this.built) this.refreshAll();
  }

  mount(container: HTMLElement): void {
    if (!this.built) this.build();
    container.appendChild(this.root);
  }

  destroy(): void {
    if (this.built && this.root.parentNode) {
      this.root.parentNode.removeChild(this.root);
    }
    this.built = false;
  }

  private build(): void {
    this.root = el("div", "para-panel");

    const title = el("div", "para-panel__title");
    title.textContent = "Paragraph";
    this.root.appendChild(title);

    const body = el("div", "para-panel__body");

    // Alignment row
    const alignRow = el("div", "para-panel__row");
    this.alignBtns = [];
    const alignments: Array<ParagraphProperties["align"]> = ["left", "center", "right", "justify"];
    const labels: Record<ParagraphProperties["align"], string> = { left: "\u2261", center: "\u2261", right: "\u2261", justify: "\u2261" };
    const titles: Record<ParagraphProperties["align"], string> = { left: "Align Left", center: "Align Center", right: "Align Right", justify: "Justify" };
    for (const a of alignments) {
      const btn = el("button", "para-panel__btn");
      btn.type = "button";
      btn.textContent = labels[a];
      btn.title = titles[a];
      btn.dataset.align = a;
      btn.style.textAlign = a === "justify" ? "justify" : a;
      btn.addEventListener("click", () => this.setAlign(a));
      alignRow.appendChild(btn);
      this.alignBtns.push(btn);
    }
    body.appendChild(alignRow);

    // Indentation rows
    this.leftIndentInput = this.makeNumberRow(body, "Left Indent");
    this.rightIndentInput = this.makeNumberRow(body, "Right Indent");
    this.firstLineIndentInput = this.makeNumberRow(body, "First Line");

    // Spacing rows
    this.spaceBeforeInput = this.makeNumberRow(body, "Space Before");
    this.spaceAfterInput = this.makeNumberRow(body, "Space After");

    this.root.appendChild(body);
    this.wireEvents();
    this.refreshAll();
    this.built = true;
  }

  private makeNumberRow(parent: HTMLElement, label: string): HTMLInputElement {
    const row = el("div", "para-panel__row");
    const lab = el("label", "para-panel__label");
    lab.textContent = label;
    const input = el("input", "para-panel__input");
    input.type = "number";
    input.spellcheck = false;
    row.appendChild(lab);
    row.appendChild(input);
    parent.appendChild(row);
    return input;
  }

  private setAlign(align: ParagraphProperties["align"]): void {
    this.props.align = align;
    this.refreshAlign();
    this.emitChange();
  }

  private wireEvents(): void {
    this.leftIndentInput.addEventListener("change", () => {
      const v = parseFloat(this.leftIndentInput.value);
      if (Number.isFinite(v)) { this.props.leftIndent = v; this.emitChange(); }
    });
    this.rightIndentInput.addEventListener("change", () => {
      const v = parseFloat(this.rightIndentInput.value);
      if (Number.isFinite(v)) { this.props.rightIndent = v; this.emitChange(); }
    });
    this.firstLineIndentInput.addEventListener("change", () => {
      const v = parseFloat(this.firstLineIndentInput.value);
      if (Number.isFinite(v)) { this.props.firstLineIndent = v; this.emitChange(); }
    });
    this.spaceBeforeInput.addEventListener("change", () => {
      const v = parseFloat(this.spaceBeforeInput.value);
      if (Number.isFinite(v)) { this.props.spaceBefore = v; this.emitChange(); }
    });
    this.spaceAfterInput.addEventListener("change", () => {
      const v = parseFloat(this.spaceAfterInput.value);
      if (Number.isFinite(v)) { this.props.spaceAfter = v; this.emitChange(); }
    });
  }

  private refreshAll(): void {
    this.leftIndentInput.value = String(this.props.leftIndent);
    this.rightIndentInput.value = String(this.props.rightIndent);
    this.firstLineIndentInput.value = String(this.props.firstLineIndent);
    this.spaceBeforeInput.value = String(this.props.spaceBefore);
    this.spaceAfterInput.value = String(this.props.spaceAfter);
    this.refreshAlign();
  }

  private refreshAlign(): void {
    for (const btn of this.alignBtns) {
      const active = btn.dataset.align === this.props.align;
      btn.classList.toggle("para-panel__btn--active", active);
    }
  }

  private emitChange(): void {
    this.onChange?.({ ...this.props });
  }
}
